/* prettier-ignore-file */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { View, TextInput, StyleSheet } from "react-native";
import { colors, radius, spacing, elevation } from "../theme";

/**
 * A row of one-digit PIN boxes. Focus advances as you type, Backspace walks back
 * (clearing the previous box when the current one is already empty), and a paste
 * lands whole in one box then spreads across the rest of the row.
 *
 * Controlled on `digits` (an array of single characters, one per box). An array
 * rather than a string because a string cannot express "box 0 filled, box 1
 * empty, box 2 filled", which is exactly what backspacing mid-row produces.
 *
 * This component never submits — the parent decides what "full" means and what
 * to do about it. Used by UserSelectionModal (login) and SwitchUserOverlay.
 */
const PinBoxes = ({ length, digits, onChange, disabled = false, autoFocus = false }) => {
  const boxRefs = useRef([]);
  const [focusedBox, setFocusedBox] = useState(0);

  // Tolerate a digits array that is short or has holes.
  const safeDigits = Array.from({ length }, (_, i) => digits?.[i] || "");

  const focusBox = useCallback(
    (index) => {
      const clamped = Math.max(0, Math.min(index, length - 1));
      setFocusedBox(clamped);
      boxRefs.current[clamped]?.focus?.();
    },
    [length]
  );

  useEffect(() => {
    boxRefs.current = boxRefs.current.slice(0, length);
  }, [length]);

  // Synchronous on purpose. iOS raises the soft keyboard for a programmatic
  // focus() only while a tap is still being processed, or when another input
  // already holds the keyboard and focus moves straight to this one. A focus
  // deferred to a later tick satisfies neither: the tap is over, and if the
  // previous input was just unmounted the keyboard has already begun closing.
  // A layout effect runs inside the same commit — inside flushSync when a tap
  // mounted the pad (AuthHeader), and while the login page's password field is
  // still mounted after sign-in (Login.js raises the pad one render before it
  // navigates away for exactly this reason).
  useLayoutEffect(() => {
    if (!autoFocus || disabled) return undefined;
    focusBox(0);
    // Inside an RN Modal the input may not be in the DOM yet when this runs;
    // the deferred retry covers that host.
    const timer = setTimeout(() => {
      if (!boxRefs.current[0]?.isFocused?.()) focusBox(0);
    }, 0);
    return () => clearTimeout(timer);
  }, [autoFocus, disabled, length, focusBox]);

  // A parent-side clear (wrong PIN) empties the row while focus is still on the
  // last box, where the retry's first digit would otherwise land — and stick,
  // since there is no box after it to advance to.
  const allEmpty = safeDigits.every((digit) => digit === "");
  const focusedBoxRef = useRef(0);
  focusedBoxRef.current = focusedBox;
  useEffect(() => {
    if (!allEmpty || !autoFocus || disabled) return;
    if (focusedBoxRef.current !== 0) focusBox(0);
  }, [allEmpty, autoFocus, disabled, focusBox]);

  const handleDigitChange = (index, text) => {
    const cleaned = text.replace(/\D/g, "");
    const next = [...safeDigits];

    if (!cleaned) {
      // Soft keyboards don't reliably emit onKeyPress, so handle an emptied box here.
      next[index] = "";
      onChange(next);
      return;
    }

    for (let i = 0; i < cleaned.length && index + i < length; i += 1) {
      next[index + i] = cleaned[i];
    }
    onChange(next);
    focusBox(index + cleaned.length);
  };

  const handleKeyPress = (index, event) => {
    if (event?.nativeEvent?.key !== "Backspace") return;
    const next = [...safeDigits];

    if (next[index]) {
      next[index] = "";
      onChange(next);
      return;
    }

    if (index > 0) {
      next[index - 1] = "";
      onChange(next);
      focusBox(index - 1);
    }
  };

  return (
    <View style={styles.row}>
      {Array.from({ length }).map((_, index) => {
        const digit = safeDigits[index];
        const filled = digit !== "";
        const active = focusedBox === index && !filled;

        return (
          <TextInput
            key={index}
            ref={(el) => {
              boxRefs.current[index] = el;
            }}
            style={[styles.box, filled && styles.boxFilled, active && styles.boxActive]}
            value={digit}
            onChangeText={(text) => handleDigitChange(index, text)}
            onKeyPress={(event) => handleKeyPress(index, event)}
            onFocus={() => setFocusedBox(index)}
            keyboardType="numeric"
            // Not maxLength={1}: a paste has to reach handleDigitChange whole so
            // it can be spread across the remaining boxes.
            maxLength={length}
            editable={!disabled}
            selectTextOnFocus
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    gap: spacing.md,
    flexWrap: "wrap",
  },
  box: {
    width: 52,
    height: 60,
    borderWidth: 2,
    borderColor: colors.border,
    borderRadius: radius.lg,
    backgroundColor: colors.surfaceMuted,
    color: colors.ink900,
    fontSize: 24,
    fontWeight: "700",
    textAlign: "center",
    outlineStyle: "none",
  },
  // Filled reads as a solid chip so the row shows progress at a glance.
  boxFilled: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
    color: colors.surface,
  },
  // Glow marks where the next digit lands.
  boxActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surface,
    boxShadow: `0 0 0 4px ${colors.primarySoft}, ${elevation.sm}`,
  },
});

export default PinBoxes;
